import { FC } from "react";
import { css } from "@emotion/react";
import Image from "next/image";
import { H7, Heading, ListItem } from "@/components/Typography";

import calendarIcon from "../../../public/images/homepage/icons/calendar.svg";
import hourglassIcon from "../../../public/images/homepage/icons/hourglass.svg";
import winIcon from "../../../public/images/homepage/icons/win.svg";
import { mq } from "@/constants/breakpoints";
import { TrustpilotBadge } from "./ScheduleGeneratorTrustBadges";
import { darkColor, darkGreenColor, mutedGreenColor } from "@/constants/colors";
import scheduleGenerator from "../../../public/images/features/how-schedule-generator-works.svg";

interface ScheduleGeneratorFeatureSectionProps {
  fields: any;
}

interface ScheduleGeneratorFeatureSectionProps {
  fields: any;
}

const ScheduleGeneratorFeatureSection: FC<ScheduleGeneratorFeatureSectionProps> = ({
  fields,
}) => {
  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
      `}
    >
      <div
        css={css`
          display: flex;
          justify-content: space-between;
          margin: 60px 0 56px 0;
          ${mq["lg"]} {
            margin: 120px 0 56px 0;
          }
          ${mq["xs"]} {
            margin-top: 24px;
            flex-direction: column;
          }
          ${mq["sm"]} {
            text-align: left;
            flex-direction: row;
          }
          ${mq["lg"]} {
            margin-top: 50px;
          }
        `}
      >
        {/* Calendar */}
        <div
          css={css`
            display: flex;
            flex-grow: 1;
            flex-direction: column;
            align-items: center;
            ${mq["xs"]} {
              flex-direction: row;
              gap: 16px;
            }
            ${mq["sm"]} {
              flex-direction: column;
              gap: 20px;
            }
          `}
        >
          <div
            css={css`
              padding: 14px;
              border-radius: 50%;
              display: flex;
              justify-content: center;
              align-items: center;
              background: linear-gradient(
                129.16deg,
                rgba(255, 255, 255, 0.8) -68.9%,
                rgba(190, 220, 255, 0.8) 131.19%
              );
            `}
          >
            <Image
              src={calendarIcon}
              alt="Calendar Icon"
              width={24}
              height={24}
            />
          </div>
          <Heading
            level={3}
            css={css`
              text-align: center;
              font-weight: 400;
              ${mq["xs"]} {
                font-size: 18px;
                text-align: left;
                font-weight: 400;
              }
              ${mq["lg"]} {
                font-size: 24px;
              }
              ${mq["sm"]} {
                text-align: center;
                max-width: 240px;
              }
            `}
          >
            Create and share schedules for free
          </Heading>
        </div>

        {/* Hourglass Icon */}
        <div
          css={css`
            display: flex;
            flex-grow: 1;
            flex-direction: column;
            align-items: center;
            align-content: center;
            ${mq["xs"]} {
              flex-direction: row;
              margin-top: 24px;
              gap: 16px;
            }
            ${mq["sm"]} {
              flex-direction: column;
              margin-top: 0;
              gap: 20px;
            }
          `}
        >
          <div
            css={css`
              padding: 14px;
              border-radius: 50%;
              display: flex;
              justify-content: center;
              align-items: center;
              background: linear-gradient(
                129.16deg,
                rgba(255, 255, 255, 0.8) -68.9%,
                rgba(190, 220, 255, 0.8) 131.19%
              );
            `}
          >
            <Image
              src={hourglassIcon}
              alt="Hourglass Icon"
              width={24}
              height={24}
            />
          </div>
          <Heading
            level={3}
            css={css`
              text-align: center;
              font-weight: 400;
              ${mq["xs"]} {
                font-size: 18px;
                text-align: left;
              }
              ${mq["lg"]} {
                font-size: 24px;
              }
              ${mq["sm"]} {
                text-align: center;
                max-width: 240px;
              }
              ${mq["md"]} {
                max-width: 303px;
              }
            `}
          >
            Generate schedules in a minute or less
          </Heading>
        </div>

        {/* Win Icon */}
        <div
          css={css`
            display: flex;
            flex-grow: 1;
            flex-direction: column;
            align-items: center;
            align-content: center;
            ${mq["xs"]} {
              flex-direction: row;
              margin-top: 24px;
              gap: 16px;
            }
            ${mq["sm"]} {
              flex-direction: column;
              margin-top: 0;
              gap: 20px;
            }
          `}
        >
          <div
            css={css`
              padding: 14px;
              border-radius: 50%;
              display: flex;
              justify-content: center;
              align-items: center;
              background: linear-gradient(
                129.16deg,
                rgba(255, 255, 255, 0.8) -68.9%,
                rgba(190, 220, 255, 0.8) 131.19%
              );
            `}
          >
            <Image src={winIcon} alt="Win Icon" width={24} height={24} />
          </div>
          <span
            css={css`
              display: inline-block;
              text-align: center;
            `}
          >
            <Heading
              level={3}
              css={css`
                text-align: center;
                font-weight: 400;
                ${mq["xs"]} {
                  font-size: 18px;
                  text-align: left;
                  margin-bottom: 2px; // todo: remove this once the spacing is fixed
                }
                ${mq["sm"]} {
                  text-align: center;
                  max-width: 240px;
                }
                ${mq["md"]} {
                  max-width: 303px;
                }
                ${mq["lg"]} {
                  font-size: 24px;
                }
              `}
            >
              Award winning Support
            </Heading>
            <a
              href="https://www.trustpilot.com/review/jerseywatch.com"
              target="_blank"
              rel="noopener noreferrer"
              css={css`
                margin-top: 5px;
              `}
            >
              <TrustpilotBadge />
            </a>
          </span>
        </div>
      </div>

      {/* How does the schedule generator work? */}
      <div
        css={css`
          background-color: #fff;
          display: flex;
          margin-top: 60px;
          ${mq["lg"]} {
            margin-top: 120px;
          }
          ${mq["xs"]} {
            flex-direction: column;
            gap: 30px;
          }
          ${mq["md"]} {
            flex-direction: row;
            gap: 44px;
            align-items: start;
            justify-items: start;
          }
          ${mq["lg"]} {
            margin-top: 120px;
            gap: 52px;
          }
          ${mq["xl"]} {
            gap: 126px;
          }
        `}
      >
        <div
          css={css`
            ${mq["md"]} {
              flex: 1;
            }
            ${mq["xs"]} {
              width: 100%;
            }
            ${mq["md"]} {
              width: auto;
            }
          `}
        >
          <Image
            src={scheduleGenerator}
            alt="Jersey Watch schedule generator ui for creating and managing sports schedules"
            // width={500}
            // height={500}
            layout="responsive"
            css={css`
              height: auto;
              max-width: 100%;
            `}
          />
        </div>
        <div
          css={css`
            flex: 1;
          `}
        >
          <Heading
            level={2}
            css={css`
              color: ${darkColor};
              margin-bottom: 30px;
              text-align: left;
              ${mq["xs"]} {
                font-size: 20px;
              }
              ${mq["lg"]} {
                font-size: 32px;
              }
            `}
          >
            {fields.howItWorksTitle}
          </Heading>
          <ul
            css={css`
              list-style-type: none;
              padding: 0;
              margin: 0;
            `}
          >
            {fields.howItWorksList.map((itemText: string, index: number) => (
              <ListItem
                key={itemText}
                css={css`
                  display: flex;
                  align-items: center;
                  margin-top: ${index === 0 ? "30px" : "24px"};
                `}
              >
                <div
                  css={css`
                    display: flex;
                    align-items: center;
                    justify-content: center;
                  `}
                >
                  <div
                    css={css`
                      width: 36px;
                      height: 36px;
                      border-radius: 50%;
                      background-color: ${mutedGreenColor};
                      display: flex;
                      justify-content: center;
                      align-items: center;
                      margin-right: 16px;
                    `}
                  >
                    <H7
                      css={css`
                        color: ${darkGreenColor};
                      `}
                    >
                      {index + 1}
                    </H7>
                  </div>
                </div>
                <Heading
                  level={3}
                  css={css`
                    color: ${darkColor};
                    font-size: 14px;
                    font-weight: 400;
                    text-align: left;
                    ${mq["lg"]} {
                      font-size: 18px;
                    }
                  `}
                >
                  {itemText}
                </Heading>
              </ListItem>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ScheduleGeneratorFeatureSection;
